import React, { useCallback, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useTranslation } from 'react-i18next';
import { getMapOptions } from '../../../utils/helpers/getMapOptions';
import useScreenSize from '../../../hooks/useScreenSize';
import { MapProps } from './types';
import { getGoogleApiKey } from '../../../utils/helpers/common';
import { getLangDefinition } from '../../../utils/helpers';
import { MapContainer, Pin, ShadowDot } from './styled';

export const Map: React.FC<MapProps> = ({
  isOpen,
  coordinates,
  setCoordinates,
  pinSelected,
  isMarkerDraggable = true
}) => {
  const {
    i18n: { language }
  } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const { isWebView } = useBridgeState();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleApiKey(),
    language: getLangDefinition(language),
    region: 'AZ'
  });

  const { isSmall } = useScreenSize();
  const { mapOptions, mapStyle, mapHeight } = getMapOptions(
    isOpen,
    isSmall,
    isWebView,
    isMarkerDraggable
  );

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      if (!isMarkerDraggable) return;

      map.addListener('dragstart', () => {
        setIsDragging(true);
      });
      map.addListener('dragend', () => {
        setIsDragging(false);
        const mapCenter = map.getCenter();
        if (mapCenter && setCoordinates) {
          setCoordinates({ lat: mapCenter.lat(), lng: mapCenter.lng() });
        }
      });
    },
    [setCoordinates]
  );

  if (!isLoaded) return null;

  return (
    <MapContainer $height={mapHeight}>
      <GoogleMap
        zoom={14}
        options={{ ...mapOptions }}
        center={coordinates}
        mapContainerStyle={mapStyle}
        onLoad={handleLoad}
      >
        {!isMarkerDraggable && coordinates && (
          <Marker
            draggable={isMarkerDraggable}
            position={coordinates}
            icon="/static/images/new_ad/icons/icon-map-pin.png"
          />
        )}
      </GoogleMap>
      {isMarkerDraggable && (pinSelected || isOpen) && coordinates && (
        <>
          <ShadowDot $dragging={isDragging} />
          <Pin $dragging={isDragging}>
            <img src="/static/images/new_ad/icons/icon-map-pin.png" alt="pin" />
          </Pin>
        </>
      )}
    </MapContainer>
  );
};
